import React from 'react';
import styled, { css } from 'styled-components';
import { PropTypes } from 'prop-types';
import profilePlaceholderImage from 'assets/img/profile_placeholder.png';
import { useAuthStore } from 'stores/authStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useFetchRegionConfig } from 'api/regions/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { STORE } from 'constants/routes';
import useSelectedRetailerProfile from 'hooks/useSelectedRetailerProfile';
import SwitcherDropdown from './SwitcherDropdown';

const ActiveBrandName = styled.span`
  font-size: 0.9rem;
  color: #fff;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActiveRegionName = styled.span`
  font-size: 0.7rem;
  color: #ccc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileItem = styled.li`
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: #444;
  }
`;

const BrandLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
`;

const NestedBrandLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
`;

const ActiveProfileName = styled.div`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileName = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.span`
  font-size: 0.8rem;
  color: #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RegionName = styled.span`
  font-size: 0.7rem;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
  gap: 12px;
`;

const NestedBrandLogoWrapper = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${({ isActive }) =>
    isActive &&
    css`
      border: 1px solid #be1622;
      border-radius: 6px;
      padding: 4px;
    `}
`;

const ProfileSwitcher = ({ compact, profileSwitcherOpened, setProfileSwitcherOpened }) => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const selectedRetailerProfile = useSelectedRetailerProfile();
  const setSelectedProfile = useSelectedProfileStore((state) => state.setSelectedProfile);
  const { data: regionConfigData } = useFetchRegionConfig(selectedRetailerProfile?.region?.uid);
  const regionConfig = regionConfigData?.data;

  const hasManyProfiles = user?.profiles?.length > 1;

  const navigate = useNavigate();

  const handleProfileClick = (profile, e) => {
    e.stopPropagation();
    queryClient.resetQueries();
    setSelectedProfile({ brandUid: profile.brand.uid, regionUid: profile.region.uid });
    if (profileSwitcherOpened && hasManyProfiles) {
      setProfileSwitcherOpened(false);
    }
    navigate(STORE);
  };

  if (!user || !user.profiles || user.profiles.length === 0) {
    return null;
  }

  const activeProfileAvatar = user.retailer_accounts?.find(
    (acc) => acc.uid === selectedRetailerProfile?.uid,
  )?.picture;
  const defaultRetailerImage = regionConfig?.default_retailer_image;

  const handleImageError = (e, retailerImage) => {
    if (e.target.src === retailerImage && defaultRetailerImage) {
      e.target.src = defaultRetailerImage;
    } else {
      e.target.src = profilePlaceholderImage;
    }
  };

  return (
    <SwitcherDropdown
      compact={compact}
      isOpen={profileSwitcherOpened}
      hasManyItems={hasManyProfiles}
      onToggle={() => setProfileSwitcherOpened((prev) => !prev)}
      icon={
        <BrandLogo
          src={activeProfileAvatar ?? defaultRetailerImage ?? profilePlaceholderImage}
          onError={(e) => handleImageError(e, activeProfileAvatar)}
        />
      }
      content={
        !compact && (
          <ActiveProfileName>
            <ActiveBrandName>{selectedRetailerProfile?.brand?.name.toUpperCase()}</ActiveBrandName>
            <ActiveRegionName>
              {selectedRetailerProfile?.region?.name.toUpperCase()}
            </ActiveRegionName>
          </ActiveProfileName>
        )
      }
    >
      {user.profiles.map((profile) => {
        const avatarProfile = user.retailer_accounts?.find(
          (acc) => acc.uid === profile.uid,
        )?.picture;

        return (
          <ProfileItem key={profile.uid} onClick={(e) => handleProfileClick(profile, e)}>
            <ProfileInfo>
              <NestedBrandLogoWrapper isActive={selectedRetailerProfile?.uid === profile.uid}>
                <NestedBrandLogo
                  src={avatarProfile ?? defaultRetailerImage ?? profilePlaceholderImage}
                  onError={(e) => handleImageError(e, avatarProfile)}
                />
              </NestedBrandLogoWrapper>
              <ProfileName>
                <BrandName>{profile.brand.name.toUpperCase()}</BrandName>
                <RegionName>{profile.region.name.toUpperCase()}</RegionName>
              </ProfileName>
            </ProfileInfo>
          </ProfileItem>
        );
      })}
    </SwitcherDropdown>
  );
};

ProfileSwitcher.propTypes = {
  compact: PropTypes.bool,
  profileSwitcherOpened: PropTypes.bool,
  setProfileSwitcherOpened: PropTypes.func,
};

export default ProfileSwitcher;
